import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';
import { exportCsv, exportXls, type Sheet } from '../lib/export';

type Line = { debit: number; credit: number; description: string | null; account: { code: string; name: string; account_type: string } | null };
type Entry = { id: string; entry_date: string; memo: string | null; source_type: string; journal: { code: string } | null; period: { name: string } | null; lines: Line[] };

export function Exports() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(`${year}-12-31`);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('journal_entries')
      .select('id,entry_date,memo,source_type,journal:accounting_journals(code),period:accounting_periods(name),lines:journal_entry_lines(debit,credit,description,account:chart_accounts(code,name,account_type))')
      .is('deleted_at', null).gte('entry_date', from).lte('entry_date', to).order('entry_date');
    setEntries((data as any) ?? []);
    setLoading(false);
  }, [from, to]);
  useEffect(() => { void load(); }, [load]);

  // flat ledger rows
  const ledger = useMemo(() => {
    const rows: (string | number)[][] = [];
    for (const e of entries) for (const l of e.lines ?? []) {
      rows.push([e.entry_date, e.journal?.code ?? '', e.memo ?? '', l.account?.code ?? '', l.account?.name ?? '', Number(l.debit), Number(l.credit)]);
    }
    return rows;
  }, [entries]);
  const ledgerHeaders = [t('exp.date'), t('exp.journal'), t('exp.memo'), t('exp.accountCode'), t('exp.account'), t('acc.debit'), t('acc.credit')];

  const trial = useMemo(() => {
    const m = new Map<string, { code: string; name: string; type: string; d: number; c: number }>();
    for (const e of entries) for (const l of e.lines ?? []) {
      const k = l.account?.code ?? '—';
      const r = m.get(k) ?? { code: k, name: l.account?.name ?? '', type: l.account?.account_type ?? '', d: 0, c: 0 };
      r.d += Number(l.debit); r.c += Number(l.credit); m.set(k, r);
    }
    return [...m.values()].sort((a, b) => a.code.localeCompare(b.code)).map((r) => [r.code, r.name, r.type, r.d, r.c, r.d - r.c] as (string | number)[]);
  }, [entries]);
  const trialHeaders = [t('exp.accountCode'), t('exp.account'), t('exp.type'), t('acc.debit'), t('acc.credit'), t('exp.balance')];

  const entryRows = useMemo(() => entries.map((e) => {
    const d = (e.lines ?? []).reduce((s, l) => s + Number(l.debit), 0);
    return [e.entry_date, e.journal?.code ?? '', e.period?.name ?? '', e.memo ?? '', e.source_type, d] as (string | number)[];
  }), [entries]);
  const entryHeaders = [t('exp.date'), t('exp.journal'), t('exp.period'), t('exp.memo'), t('exp.source'), t('exp.total')];

  // journals: one sheet per journal for Excel; flat for CSV
  const byJournal = useMemo(() => {
    const m = new Map<string, (string | number)[][]>();
    for (const e of entries) for (const l of e.lines ?? []) {
      const j = e.journal?.code ?? '—';
      const arr = m.get(j) ?? [];
      arr.push([e.entry_date, e.memo ?? '', l.account?.code ?? '', l.account?.name ?? '', Number(l.debit), Number(l.credit)]);
      m.set(j, arr);
    }
    return m;
  }, [entries]);
  const journalHeaders = [t('exp.date'), t('exp.memo'), t('exp.accountCode'), t('exp.account'), t('acc.debit'), t('acc.credit')];

  const period = `${from}_${to}`;
  const EXPORTS = [
    { key: 'ledger', csv: () => exportCsv(`ledger_${period}`, ledgerHeaders, ledger), xls: () => exportXls(`ledger_${period}`, [{ name: 'Ledger', headers: ledgerHeaders, rows: ledger }]) },
    { key: 'trial', csv: () => exportCsv(`trial_balance_${period}`, trialHeaders, trial), xls: () => exportXls(`trial_balance_${period}`, [{ name: 'Trial balance', headers: trialHeaders, rows: trial }]) },
    { key: 'journals', csv: () => exportCsv(`journals_${period}`, [t('exp.journal'), ...journalHeaders], [...byJournal.entries()].flatMap(([j, rows]) => rows.map((r) => [j, ...r]))), xls: () => exportXls(`journals_${period}`, [...byJournal.entries()].map(([j, rows]): Sheet => ({ name: j, headers: journalHeaders, rows }))) },
    { key: 'entries', csv: () => exportCsv(`entries_${period}`, entryHeaders, entryRows), xls: () => exportXls(`entries_${period}`, [{ name: 'Entries', headers: entryHeaders, rows: entryRows }]) },
  ];

  return (
    <Layout header={<Topbar />}>
      <div className="px-7 py-6">
        <h1 className="mb-1 text-xl font-semibold">{t('nav.exports')}</h1>
        <p className="mb-5 text-sm text-muted">{t('exp.subtitle')}</p>

        <Card className="mb-5 flex flex-wrap items-end gap-3 p-4">
          <label className="flex flex-col gap-1 text-sm"><span className="text-xs text-muted">{t('pnl.from')}</span><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-md border border-border bg-card px-2 py-1.5" /></label>
          <label className="flex flex-col gap-1 text-sm"><span className="text-xs text-muted">{t('pnl.to')}</span><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-md border border-border bg-card px-2 py-1.5" /></label>
          <span className="pb-1.5 text-xs text-muted">{loading ? t('common.loading') : t('exp.entriesInRange', { n: entries.length })}</span>
        </Card>

        <div className="grid gap-3 sm:grid-cols-2">
          {EXPORTS.map((x) => (
            <Card key={x.key} className="flex items-center justify-between p-4">
              <div><div className="text-sm font-medium">{t('exp.' + x.key)}</div><div className="text-xs text-muted">{t('exp.' + x.key + '_desc')}</div></div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={x.csv} disabled={loading}>CSV</Button>
                <Button variant="ghost" onClick={x.xls} disabled={loading}>Excel</Button>
              </div>
            </Card>
          ))}
        </div>
        <p className="mt-4 text-xs text-muted">{t('exp.noFiscal')}</p>
      </div>
    </Layout>
  );
}
